<template>
    <v-row>
        <v-col cols="12" class="title-label" v-if="!noTitle">
            {{ $t('entrance.basic_info') }}
        </v-col>
        <v-col cols="12">
            <kimsa-text-field
                :label="$t('company.name')"
                :placeholder="$t('company.name_placeholder')"
                :rules="rules.name"
                @change="name = $event"
                :value="name"
            />
        </v-col>
        <v-col cols="12">
            <kimsa-combobox
                :label="$t('entrance.country')"
                :placeholder="$t('entrance.country_placeholder')"
                :items="countryOptions"
                item-text="label"
                icon="mdi-web"
                with-selected-dot
                @change="country = $event"
                :value="country"
                ref="country"
            />
        </v-col>
        <v-col cols="12">
            <kimsa-phone-field
                :label="$t('entrance.phone')"
                :placeholder="$t('entrance.phone_placeholder')"
                :value="phone"
                :code="phoneCode"
                @change="phone = $event"
                @change:code="onSelectPhoneCode"
            />
        </v-col>
        <v-col cols="12">
            <kimsa-text-field
                :label="$t('company.address')"
                :placeholder="$t('company.address_placeholder')"
                @change="address = $event"
                :value="address"
            />
        </v-col>
        <v-col cols="12">
            <span class="label">{{ $t('company.type') }}</span>
            <div class="segment-btns">
                <button
                    v-for="opt in typesOptions"
                    :key="opt.value"
                    type="button"
                    :class="['segment-btn', { 'segment-btn--active': type === opt.value }]"
                    @click="type = opt.value"
                >
                    {{ opt.name }}
                </button>
            </div>
        </v-col>
    </v-row>
</template>

<script>

import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import KimsaPhoneField from "@/components/common/inputs/KimsaPhoneField";

import {mapActions, mapState} from 'vuex'
import {debounce} from "@/utils/Common";

export default {
    name: "BasicInfo",
    components: {
        KimsaTextField,
        KimsaCombobox,
        KimsaPhoneField,
    },
    props: {
        stepperMode: {
            type: Boolean,
            default: false
        },
        noTitle: {
            type: Boolean,
            default: false
        }
    },
    data(vm) {
        return {
            name: '',
            country: '',
            phone: '',
            phoneCode: '',
            phoneCodeTouched: false,
            address: '',
            type: '',

            typesOptions: [
                {name: vm.$t('company.types.club'), value: 'club'},
                {name: vm.$t('company.types.gym'), value: 'gym'},
                {name: vm.$t('company.types.institution'), value: 'institution'}
            ]
        }
    },
    computed: {
        ...mapState(['countries']),
        countryOptions(vm = this) {
            return vm.countries.map(country => ({ ...country, id: country.id, label: vm.countryLabel(country.name) }))
        },
        countryPayload(vm = this) {
            const country = vm.country
            if (!country || !country.id) return ''
            return { id: country.id, name: country.name || '', phoneCode: country.phoneCode || '' }
        },
        form(vm = this) {
            return {
                name: vm.name,
                phone: vm.phone,
                phoneCode: vm.phoneCode,
                country: vm.countryPayload,
                address: vm.address,
                type: vm.type,
            }
        },
        rules(vm = this) {
            return {
                name: [
                    v => !!v || vm.$t('rules.required'),
                    v => v && v.length >= 3 || vm.$t('rules.min_length', {num: '3'})
                ],
                phone: [
                    v => !!v || vm.$t('rules.required'),
                    v => v && v.length >= 8 || vm.$t('rules.min_length', {num: '8'})
                ]
            }
        }
    },
    async mounted() {
        const vm = this
        if (vm.countries.length === 0) await vm.bindCountries()
        vm.loadAccount()
    },
    methods: {
        ...mapActions(['bindCountries']),
        onSelectPhoneCode(code) {
            const vm = this
            vm.phoneCode = code || ''
            vm.phoneCodeTouched = true
        },
        loadAccount() {
            const vm = this
            vm.name = vm.account?.name || ''
            vm.country = vm.countryOptions.find(country => country.name === vm.account?.country?.name) || ''
            vm.phone = vm.account?.phone || ''
            vm.address = vm.account?.address || ''
            vm.type = vm.account?.type || ''
            if (vm.$refs.country) vm.$refs.country.model = vm.country
            if (vm.account?.phoneCode) {
                vm.phoneCode = vm.account.phoneCode
                vm.phoneCodeTouched = true
            } else {
                vm.phoneCode = vm.account?.country?.phoneCode || ''
            }
        },
        emitNow() {
            this.$emit('change', this.form)
        },
        debouncedEmit: debounce(
            function () {
                this.$emit('change', this.form)
            }, 1000),
    },
    watch: {
        account() {
            if (!this.stepperMode) this.loadAccount()
        },
        country(val) {
            if (!this.phoneCodeTouched) this.phoneCode = val?.phoneCode || ''
        },
        form() {
            this.debouncedEmit()
        },
    },
}
</script>

<style scoped>

.title-label {
    font-size: 26px;
    font-weight: 800;
    letter-spacing: -0.02em;
    padding-bottom: 16px;
}

.label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtSecondary-base);
    margin-bottom: 6px;
}

.segment-btns {
    display: flex;
    gap: 8px;
}

.segment-btn {
    flex: 1;
    height: 40px;
    border-radius: 8px;
    border: 1px solid var(--v-outline-base);
    background: var(--v-surface-base);
    color: inherit;
    font-size: 14px;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    outline: none;
    transition: border-color .15s ease, background-color .15s ease, color .15s ease;
}

.segment-btn--active {
    background: rgba(102, 204, 204, 0.12);
    border-color: var(--v-secondary-base);
    color: var(--v-accent-base);
    font-weight: 600;
}

</style>
